// src/pages/Home/Home.js
import React, { useEffect, useState, useRef, useMemo } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import SEO from "../../components/SEO";
import { useForm } from "react-hook-form";
import {
  GoogleMap,
  Marker,
  OverlayView,
  useJsApiLoader,
} from "@react-google-maps/api";
import EventRow from "../../components/EventRow";
import { formatEventDates } from "../../utils/dateUtils";
import { getCityCoords } from "../../utils/maputils";
import { LoadingThreeDot } from "../../opera-ui/LoadingThreeDot";
import {
  defaultCenter,
  mapContainerStyle,
  slides,
} from "./_components/constants";
import EventSearchForm from "./_components/filter-events-search-form";
import slugify from "../../utils/slugify";
import { useTranslation } from "react-i18next";

function Home() {
  const [events, setEvents] = useState([]);
  const [eventsSearch, setEventsSearch] = useState({});
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [eventCities, setEventCities] = useState([]);
  const upcomingPerformanceRef = useRef(null);
  const { hash } = useLocation();
  const [isEventSearchLoading, setEventSearchLoading] = useState(false);
  const { register, handleSubmit, control } = useForm();
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { t } = useTranslation();

  // Helper to normalize URLs to absolute (for JSON-LD only)
  const normalizeUrl = (url) => {
    if (!url) return undefined;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/")) return `https://www.operatoday.org${url}`;
    return `https://www.operatoday.org/${url}`;
  };

  const loadMoreEvents = React.useCallback(
    async (data, customSkip) => {
      if (isEventSearchLoading) return;
      setEventSearchLoading(true);
      const currentSkip = typeof customSkip === "number" ? customSkip : skip;
      const search =
        data?.eventSearch || data?.startDate || data?.endDate
          ? data
          : eventsSearch;
      await new Promise((resolve) => setTimeout(resolve, 500));
      const res = await axios.get(
        `/api/events/search?limit=50&skip=${currentSkip}&eventSearch=${
          search.eventSearch || ""
        }&startDate=${search.startDate || ""}&endDate=${search.endDate || ""}`
      );
      if (res.data.length < 50) setHasMore(false);
      setEvents((prev) => [...prev, ...res.data]);
      setSkip((prev) => prev + 50);
      setEventSearchLoading(false);
    },
    [isEventSearchLoading, skip, eventsSearch]
  );

  useEffect(() => {
    if (!hasMore) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isEventSearchLoading) {
          loadMoreEvents();
        }
      },
      { threshold: 1 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, isEventSearchLoading, eventsSearch, skip, loadMoreEvents]);

  useEffect(() => {
    if (skip !== 0) return;
    if (hash === "#upcoming-performance" || upcomingPerformanceRef.current) {
      setTimeout(() => {
        const element = upcomingPerformanceRef.current;
        const offset = 70;
        const elementTop = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementTop - offset,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [hash, isEventSearchLoading, skip]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyBxuKcDrcwD7JX2PY4LDH_EODW_5nWxIYs",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [venuesRes] = await Promise.all([axios.get("/api/venues")]);
        const venuesData = venuesRes.data;
        const venuesWithCoords = await Promise.all(
          venuesData.map(async (venue, index) => {
            const coords = await getCityCoords(
              venue?.cityNew?.name,
              venue.country,
              index,
              venue.street || venue.name
            );
            return {
              city: venue?.cityNew?.name,
              name: venue.name,
              position: coords,
            };
          })
        );
        setEventCities(venuesWithCoords);
      } catch {
        // ignore
      }
    };
    fetchData();
  }, []);

  const translatedSlides = useMemo(
    () =>
      slides.map((s, idx) => ({
        ...s,
        title: t(`home.slides.${idx}.title`),
        text: t(`home.slides.${idx}.text`),
      })),
    [t]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (imageLoaded) {
        setCurrentSlide((prev) => (prev + 1) % translatedSlides.length);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [imageLoaded, translatedSlides]);

  const goToPreviousSlide = () =>
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  const goToNextSlide = () =>
    setCurrentSlide((prev) => (prev + 1) % slides.length);

  const filtereventsBySearch = (data) => {
    setEventsSearch(data);
    setSkip(0);
    setEvents([]);
    setHasMore(true);
    loadMoreEvents(data, 0);
  };

  // ------------------ ONLY SEO (JSON-LD) BELOW ------------------

  // Future-only filter helper

  const now = new Date();
  const earliestIso = (date_times) => {
    if (!Array.isArray(date_times) || date_times.length === 0) return undefined;
    const min = Math.min(...date_times.map((d) => new Date(d.date).getTime()));
    return isFinite(min) ? new Date(min).toISOString() : undefined;
  };

  const futureEvents = events
    .filter(
      (event) =>
        event &&
        event._id &&
        Array.isArray(event.date_times) &&
        event.date_times.length > 0
    )
    .filter((event) => {
      const iso = earliestIso(event.date_times);
      return iso ? new Date(iso).getTime() >= now.getTime() : false;
    })
    .slice(0, 50); // keep 50 for the rich results test success

  // Build simple ItemList entries (name + url + position)
  const itemListElement = futureEvents.map((event, idx) => {
    const venueName = event.venue?.name || "Unknown venue";
    const venueSlug = slugify(venueName);
    const pagePath = `/${encodeURIComponent(venueSlug)}/${encodeURIComponent(
      event.eventSlug
    )}`;
    const canonicalUrl = normalizeUrl(pagePath);
    const label = `${event.title || "Untitled"} — ${venueName}`;
    return {
      "@type": "ListItem",
      position: idx + 1,
      url: canonicalUrl,
      name: label,
    };
  });

  const homepageSchema = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://www.operatoday.org/",
      url: "https://www.operatoday.org/",
      name: "OperaToday - Opera, Festival, and Events",
      description: "Discover upcoming opera, festival, and Events.",
    },
    {
      "@context": "https://schema.org",
      "@type": "Website",
      "@id": "https://www.operatoday.org/#website",
      url: "https://www.operatoday.org/",
      name: "OperaToday",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://www.operatoday.org/?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Upcoming performances",
      itemListElement,
    },
  ];

  // ------------------ END ONLY SEO ------------------

  if (!isLoaded) return <div className="text-center">Loading...</div>;

  return (
    <div className="pt-[60px]">
      {/* SEO Meta Tags & Structured Data */}
      <SEO
        title="Opera Today - Discover Upcoming Opera Performances Worldwide"
        description="Opera Today - Your guide to opera performances"
        canonical="https://www.operatoday.org/"
        image="https://www.operatoday.org/images/Teatro_alla_Scala.jpg"
        type="website"
        schema={homepageSchema}
      />

      <div>
        <div className="relative w-full min-h-[200px] h-[85vh] sm:h-[95vh] top-0">
          <img
            src="/images/Teatro_alla_Scala.jpg"
            alt="EXPERIENCE OPERA TRIPS"
            className={`w-full h-full object-cover object-[80%_20%] aspect-7/4 ${
              currentSlide === 0 ? "" : "hidden"
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          <img
            src="/images/performer_example.jpg"
            alt="DISCOVER LONDON’S FINEST"
            className={`w-full h-full object-cover ${
              currentSlide === 1 ? "" : "hidden"
            }`}
          />
          <img
            src="/images/composers-hero.jpg"
            alt="PARISIAN ELEGANCE"
            className={`w-full h-full object-cover object-[50%_30%] sm:object-[50%_37%] lg:object-[50%_45%]  ${
              currentSlide === 2 ? "" : "hidden"
            }`}
          />

          <button
            className="absolute top-1/2 -translate-y-1/2 left-4 text-xl cursor-pointer hover:scale-110 transition-all z-10"
            onClick={goToPreviousSlide}
          >
            <svg
              className="w-6 h-6 text-white hover:text-gray-300"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m15 19-7-7 7-7"
              />
            </svg>
          </button>
          <button
            className="absolute top-1/2 -translate-y-1/2 right-4 text-xl cursor-pointer hover:scale-110 transition-all z-10"
            onClick={goToNextSlide}
          >
            <svg
              className="w-6 h-6 text-white hover:text-gray-300"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m9 5 7 7-7 7"
              />
            </svg>
          </button>
          {imageLoaded && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-white z-10">
              <h1
                className="text-[2.5rem] md:text-[4.4rem] mb-4 text-[#ffff] font-cormorant-garamond font-medium whitespace-pre-line w-full"
                style={{
                  textShadow: "2px 2px  rgba(0,0,0)",
                }}
              >
                {translatedSlides[currentSlide].title}
              </h1>
              <p
                className="text-xl md:text-4xl text-white font-cormorant-garamond font-normal"
                style={{
                  textShadow:
                    "1px 1px 3px rgba(0,0,0,0.8), -1px -1px 2px rgba(0,0,0,0.6), 0 0 6px rgba(0,0,0,0.4)",
                }}
              >
                {translatedSlides[currentSlide].text}
              </p>
            </div>
          )}
          {/* <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {slides.map((_, index) => (
              <span
                key={index}
                className={`w-3 h-3 rounded-full cursor-pointer transition-colors ${index === currentSlide ? "bg-opera-red" : "bg-white"
                  }`}
                onClick={() => setCurrentSlide(index)}
              ></span>
            ))}
          </div> */}
          <div className="absolute bottom-16 right-0 -translate-x-1/2 flex gap-4 z-10">
            {translatedSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentSlide ? "bg-white" : "bg-transparent"
                } border border-gray-300`}
                aria-label={`Go to slide ${index + 1}`}
                style={{ outline: "none" }}
              />
            ))}
          </div>
        </div>
      </div>

      <section className="py-8">
        <section className="mb-2 max-w-[1250px] mx-auto px-4 flex flex-col">
          <h2 className="text-2xl mb-4 mt-8 text-left">{t("home.overview")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:px-1">
            <NavLink
              to="/opera-houses-and-festivals"
              className="flex flex-col justify-center w-full xl:w-[389px] xl:h-[522px] lg:w-[294px] lg:h-[487px] md:h-[490px] md:w-[345px] bg-white border border-gray-300 overflow-hidden shadow-md hover:scale-[1.02] transition-transform no-underline text-inherit"
            >
              <img
                src="/images/venue_example.jpg"
                alt="Opera Houses"
                className="w-full h-[358px] lg:h-[291px] xl:h-[358px] object-cover mx-auto"
              />
              <div className="p-4 flex flex-col justify-between flex-1">
                <div>
                  <h3 className="text-xl text-black mb-2">
                    {t("home.destinations")}
                  </h3>
                  <p className="text-black text-base">
                    {t("home.destinationsSubtitle")}
                  </p>
                </div>
                <span className="bg-black text-[#DFDFDF] mt-2 px-4 py-2 text-sm rounded inline-block hover:text-white transition-colors self-start">
                  {t("home.more")}
                </span>
              </div>
            </NavLink>

            <NavLink
              to="/performers"
              className="flex flex-col w-full xl:w-[389px] xl:h-[522px] lg:w-[294px] lg:h-[487px] md:h-[490px] md:w-[345px] bg-white border border-gray-300 overflow-hidden shadow-md hover:scale-[1.02] transition-transform no-underline text-inherit"
            >
              <img
                src="/images/performer_example.jpg"
                alt="Performers"
                className="w-full h-[358px] lg:h-[291px] xl:h-[358px] object-cover mx-auto"
              />
              <div className="p-4 flex flex-col justify-between flex-1">
                <div>
                  <h3 className="text-xl text-black mb-2">
                    {t("home.performers")}
                  </h3>
                  <p className="text-black text-base">
                    {t("home.performersSubtitle")}
                  </p>
                </div>
                <span className="bg-black text-[#DFDFDF] mt-2 px-4 py-2 text-sm rounded inline-block hover:text-white transition-colors self-start">
                  {t("home.more")}
                </span>
              </div>
            </NavLink>

            <NavLink
              to="/composers"
              className="flex flex-col w-full xl:w-[389px] xl:h-[522px] lg:w-[294px] lg:h-[487px] md:h-[490px] md:w-[345px] bg-white border border-gray-300 overflow-hidden shadow-md hover:scale-[1.02] transition-transform no-underline text-inherit"
            >
              <img
                src="/images/composers-hero.jpg"
                alt="Composers"
                className="w-full h-[358px] lg:h-[291px] xl:h-[358px] object-cover mx-auto"
              />
              <div className="p-4 flex flex-col justify-between flex-1">
                <div>
                  <h3 className="text-xl text-black mb-2">
                    {" "}
                    {t("home.composers")}
                  </h3>
                  <p className="text-black text-base">
                    {t("home.composersSubtitle")}
                  </p>
                </div>
                <span className="bg-black text-[#DFDFDF] mt-2 px-4 py-2 text-sm rounded inline-block hover:text-white transition-colors self-start">
                  {t("home.more")}
                </span>
              </div>
            </NavLink>
          </div>
        </section>

        <section
          id="upcoming-performance"
          ref={upcomingPerformanceRef}
          className="hidden"
        >
          <div
            className="relative bg-cover bg-center text-gray-200"
            style={{ backgroundImage: "url('/images/Teatro_alla_Scala.jpg')" }}
          >
            <div className="bg-black bg-opacity-70 pt-2 py-2 sm:py-8 sm:pt-16 mt-16">
              <div className="flex flex-col items-center z-20">
                <h2
                  className={`text-2xl flex sm:w-[350px] text-gray-200 text-center sm:mb-4 capitalize ${
                    skip > 0 ? "hidden sm:block" : "block"
                  }`}
                >
                  {t("home.upcomingPerformances")}
                </h2>
                <EventSearchForm
                  onSubmit={filtereventsBySearch}
                  register={register}
                  control={control}
                  handleSubmit={handleSubmit}
                  isEventSearchLoading={isEventSearchLoading}
                  skip={skip}
                />
              </div>
            </div>
          </div>

          <div className="overflow-y-auto and max-h-[70vh] scrollbar-hide">
            <div className="flex flex-col gap-2 max-w-[1280px] pl-6 mx-auto my-6 px-4 overflow-anchor-none">
              {events.map((event) => (
                <EventRow
                  key={event._id}
                  event={event}
                  formatEventDates={formatEventDates}
                />
              ))}

              {!isEventSearchLoading && hasMore && skip > 0 && (
                <>
                  <LoadingThreeDot />
                  <div ref={loaderRef} style={{ height: 500 }}></div>
                </>
              )}

              {isEventSearchLoading && (
                <div className="flex justify-center items-center mt-10 mb-20">
                  <LoadingThreeDot />
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="max-w-[1225px] mx-auto pl-[10px] pr-2 mt-16">
          <h2 className="text-2xl text-left mb-4">
            {t("home.destinationsSectionTitle")}
          </h2>
          <div className="relative" style={{ width: "100%" }}>
            <button
              className="absolute top-[10px] left-[179px] z-20 bg-white px-4 py-[8px] hover:bg-slate-200 rounded shadow transition"
              onClick={() => {
                window.location.href = "#";
              }}
            >
              {t("home.operatodayButton")}
            </button>

            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={defaultCenter}
              zoom={4}
            >
              {eventCities.map((venue) => (
                <Marker
                  key={venue.name}
                  position={venue.position}
                  onClick={() => setSelectedMarker(venue)}
                  icon={{
                    url: "/images/venue_0.png",
                    scaledSize: new window.google.maps.Size(40, 40),
                  }}
                >
                  {selectedMarker === venue && (
                    <OverlayView
                      position={venue.position}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div
                        className="bg-white rounded-lg shadow-lg p-4 min-w-[180px] max-w-[260px] border border-gray-200"
                        style={{
                          transform: "translate(-50%, -110%)",
                          position: "absolute",
                          left: "50%",
                          top: 0,
                          zIndex: 1000,
                        }}
                      >
                        <button
                          className="absolute top-1 right-1 text-gray-400 hover:text-black text-lg font-bold"
                          onClick={() => setSelectedMarker(null)}
                          style={{ lineHeight: 1 }}
                        >
                          ×
                        </button>
                        <Link
                          to={`/${slugify(venue.name)}`}
                          className="text-opera-red text-lg font-cinzel transition duration-150"
                          style={{ cursor: "pointer" }}
                          rel="noopener noreferrer"
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.filter = "brightness(1.5)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.filter = "brightness(1)")
                          }
                        >
                          {venue.name}
                        </Link>
                        <p className="text-gray-700 mt-1 text-sm font-medium font-lato">
                          {venue?.city}
                        </p>
                      </div>
                    </OverlayView>
                  )}
                </Marker>
              ))}
            </GoogleMap>
          </div>
        </section>
      </section>
    </div>
  );
}

export default Home;
